import { Model } from "@ofqual/portal-core/core/types";

type NullableString = string | null;
type NullableBoolean = boolean | null;
type NullableNumber = number | null;

type SocItemCore = {
  id: string;
  organisationId: number;
  orgName: string;
  createdByUpn: string;
  createdByDisplayName: string;
  modifiedByUpn: string;
  modifiedByDisplayName: string;
  referenceNumber: string;
  caseTitle: string;
  regulatorsToNotifyOfqual: boolean;
  regulatorsToNotifyCcea: boolean;
  regulatorsToNotifyQw: boolean;
  currentCompliant: NullableBoolean;
  currentNonComplianceDescription: NullableString;
  currentNonComplianceMitigation: NullableString;
  currentCompliantDate: NullableString;
  futureCompliant: NullableBoolean;
  futureNonComplianceDescription: NullableString;
  futureNonComplianceHow: NullableString;
  status: SocStatus;
  dateSubmitted: NullableString;
  createdDate: Date;
  modifiedDate: Date;
  assignedToUpn: NullableString;
  assignedToDisplayName: NullableString;
  lastResponseByUpn: NullableString;
  lastResponseByDisplayName: NullableString;
  lastResponseDate: NullableString;
  socCurrentNonCompliantGeneralCondition: number[];
  socCurrentNonCompliantQualCondition: number[];
  socCurrentNonCompliantSubjectCondition: number[];
  socFutureNonCompliantGeneralCondition: number[];
  socFutureNonCompliantQualCondition: number[];
  socFutureNonCompliantSubjectCondition: number[];
  currentRevenueIncome: NullableNumber;
  currentExpenditure: NullableNumber;
  currentProfitSurplus: NullableNumber;
  currentCashLiquidReserves: NullableNumber;
  forecastRevenueIncome: NullableNumber;
  forecastExpenditure: NullableNumber;
  forecastProfitSurplus: NullableNumber;
  forecastCashLiquidReserves: NullableNumber;
  forecastNumberOfEmployees: NullableNumber;
  forecastNumberOfContractors: NullableNumber;
  forecastFurtherInformation: NullableString;
  itRiskManagementMitigation: NullableString;
  itInsiderRiskMitigation: NullableString;
  epaOffered: NullableBoolean;
  epaChallengeInCompliance: NullableString;
  epaMitigationsDescription: NullableString;
  epaTrainingOffered: NullableBoolean;
  epaMaterialOffered: NullableBoolean;
  govBodyConfirmCompliance: NullableBoolean;
  govBodyNoCompliance: NullableString;
  govBodyConfirmApproval: NullableBoolean;
  govBodyConfirmNoApproval: NullableString;
  govBodyConfirmChairRo: NullableBoolean;
  govBodyConfirmNoChairRo: NullableString;
  govBodyConfirmNameRo: NullableString;
  govBodyConfirmNameChair: NullableString;
  govBodyConfirmSigningOnBehalfName: NullableString;
  govBodyConfirmSigningOnBehalfTitle: NullableString;
  planToBuildReserve: NullableNumber;
  planToBuildReserveDetails: NullableString;
  thirdPartyArrangement: NullableBoolean;
  thirdPartyComplySummary: NullableString;
  thirdPartyExpectation: NullableString;
  thirdPartyMonitorContingency: NullableString;
  thirdPartyMonitorContinuity: NullableString;
  thirdPartyMonitorBreach: NullableString;
  thirdPartyMonitorPerformance: NullableString;
  thirdPartyInHouseSummary: NullableString;
  thirdPartyArrangementNatureSummary: NullableString;
  sharedOperationFinance: NullableBoolean;
  accountingYearCurrentNumberOfEmployees: NullableNumber;
  accountingYearCurrentNumberOfContractors: NullableNumber;
  annualAccountingFurtherInformation: NullableString;
  monthlyFixedCost: NullableNumber;
  monthlyCoverage: NullableNumber;
  reserveThresholdLevel: NullableNumber;
  inflationImpact: NullableString;
  conflictOfInterestIdentification: NullableString;
  prohibitedTrainingFrequency: NullableString;
  prohibitedTrainingContent: NullableString;
  prohibitedTrainingComplianceDescription: NullableString;
  prohibitedTrainingMaterialFrequency: NullableString;
  prohibitedTrainingMaterialContent: NullableString;
  prohibitedTrainingMaterialComplianceDescription: NullableString;
  financialRisk: NullableString;
};

type SocItemOfqual = {
  agreeCurrentCompliant: NullableBoolean;
  agreeFutureCompliant: NullableBoolean;
  complianceDisagreement: NullableString;
  complianceRagRating: NullableNumber;
  standardsFollowUp: NullableNumber;
  standardsFollowUpStatus: NullableNumber;
  standardsClosureStatus: NullableNumber;
  organisationStabilityRagRating: NullableNumber;
  organisationFollowUp: NullableNumber;
  organisationFollowUpStatus: NullableNumber;
  organisationFollowUpLead: string;
  followUpLead: NullableNumber;
  closingStatus: NullableNumber;
  closingStatusRationale: NullableNumber;
  kloeTwoRagRating: NullableNumber;
  kloeTwoFollowUp: NullableNumber;
  kloeTwoFollowUpStatus: NullableNumber;
  kloeTwoLead: NullableString;
  kloeThreeRagRating: NullableNumber;
  kloeThreeFollowUp: NullableNumber;
  kloeThreeFollowUpStatus: NullableNumber;
  kloeThreeLead: NullableString;
  overallRagRating: NullableNumber;
  closeReason: NullableString;
  riskMarketsClosureStatus: NullableNumber;
  kloeTwoClosureStatus: NullableNumber;
  kloeThreeClosureStatus: NullableNumber;
};

export const defaultValues: Partial<SocItem> = {
  regulatorsToNotifyOfqual: false,
  regulatorsToNotifyCcea: false,
  regulatorsToNotifyQw: false,
  currentCompliant: null,
  currentNonComplianceDescription: null,
  currentNonComplianceMitigation: null,
  currentCompliantDate: null,
  futureCompliant: null,
  futureNonComplianceDescription: null,
  futureNonComplianceHow: null,
  socCurrentNonCompliantGeneralCondition: [],
  socCurrentNonCompliantQualCondition: [],
  socCurrentNonCompliantSubjectCondition: [],
  socFutureNonCompliantGeneralCondition: [],
  socFutureNonCompliantQualCondition: [],
  socFutureNonCompliantSubjectCondition: [],
  currentRevenueIncome: null,
  currentExpenditure: null,
  currentProfitSurplus: null,
  currentCashLiquidReserves: null,
  forecastRevenueIncome: null,
  forecastExpenditure: null,
  forecastProfitSurplus: null,
  forecastCashLiquidReserves: null,
  itRiskManagementMitigation: null,
  itInsiderRiskMitigation: null,
  epaOffered: null,
  govBodyConfirmCompliance: null,
  govBodyNoCompliance: null,
  govBodyConfirmApproval: null,
  govBodyConfirmNoApproval: null,
  govBodyConfirmChairRo: null,
  govBodyConfirmNoChairRo: null,
  govBodyConfirmNameRo: null,
  govBodyConfirmNameChair: null,
  govBodyConfirmSigningOnBehalfName: null,
  govBodyConfirmSigningOnBehalfTitle: null,
  closeReason: null,
  thirdPartyArrangement: null,
  sharedOperationFinance: null,
  epaTrainingOffered: null,
  epaMaterialOffered: null,
  monthlyCoverage: null,
  monthlyFixedCost: null,
  financialRisk: null,
  inflationImpact: null,
  planToBuildReserve: null,
  reserveThresholdLevel: null,
  forecastFurtherInformation: null,
  forecastNumberOfContractors: null,
  forecastNumberOfEmployees: null,
  annualAccountingFurtherInformation: null,
  accountingYearCurrentNumberOfEmployees: null,
  accountingYearCurrentNumberOfContractors: null,
};

export type SocItem = SocItemCore & SocItemOfqual;

export const requiredOfqualFields: (keyof SocItemOfqual)[] = [
  "agreeCurrentCompliant",
  "agreeFutureCompliant",
  "complianceRagRating",
  "standardsFollowUp",
  "standardsFollowUpStatus",
  "organisationStabilityRagRating",
  "organisationFollowUpStatus",
  "organisationFollowUp",
  "kloeTwoRagRating",
  "kloeTwoFollowUp",
  "kloeTwoFollowUpStatus",
  "kloeTwoLead",
  "overallRagRating",
  "closingStatus",
  "closingStatusRationale",
  "closeReason",
];

export const ofqualKeys: (keyof SocItemOfqual)[] = [
  ...requiredOfqualFields,
  "kloeThreeRagRating",
  "kloeThreeFollowUp",
  "kloeThreeFollowUpStatus",
  "kloeThreeLead",
];

export enum SocStatus {
  New = 0,
  Draft = 1,
  Submitted = 2,
  InReview = 3,
  ReviewMeetingHeld = 4,
  Closed = 5,
}

export const fromApiModel = (model: Model): SocItem => {
  return {
    ...model,
  } as SocItem;
};

export const toApiModel = (item: SocItem): Model => {
  return {
    ...item,
  } as Model;
};

export enum PlanToBuildReserveStatus {
  Yes = 1,
  No = 2,
  "N/A" = 3,
}
